import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import iziToast from "izitoast";
import {
  Camera,
  Eye,
  EyeOff,
  Lock,
  Check,
  Mail,
  User,
  Phone,
  Building2,
  MapPin,
  FileText,
  CreditCard,
  ShieldCheck,
  Sparkles,
  BadgeCheck,
  Clock3,
  ArrowRight,
} from "lucide-react";
import PageWrapper from "components/PageWrapper";
import Spinner from "components/Spinner";
import { setUserTokenAction } from "../redux/userSlice";
import { getAccountAPI, saveBillingAPI } from "actions/companyActions";
import { formatDate } from "utils/helper";

const STRENGTH = ["", "Weak", "Fair", "Good", "Strong"];
const STRENGTH_COLOR = ["", "#C50F1F", "#BC4B09", "#0F6CBD", "#107C10"];
const COUNTRIES = [
  "India",
  "United States",
  "United Kingdom",
  "Germany",
  "Singapore",
  "United Arab Emirates",
  "Australia",
];

const pwScore = (pw) => {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s; // 0..4
};

const getInitials = (first, last, name) => {
  const a = (first || "").trim();
  const b = (last || "").trim();
  if (a || b) return `${a[0] || ""}${b[0] || ""}`.toUpperCase() || "U";
  return (
    (name || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("") || "U"
  );
};

const inputCls =
  "h-11 w-full px-3.5 bg-slate-50/90 border border-slate-200 rounded-xl text-sm text-slate-800 outline-none transition-all shadow-sm focus:border-s360-violet focus:ring-4 focus:ring-s360-violet/10 placeholder:text-slate-400";

const PwInput = ({ value, onChange, placeholder, invalid }) => {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative flex items-center">
      <input
        className={`${inputCls} w-full pr-10 ${invalid ? "border-s360-danger border-b-s360-danger" : ""}`}
        type={shown ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <button
        type="button"
        className="absolute right-2 w-7 h-7 inline-flex items-center justify-center text-s360-fg3 hover:text-s360-fg1"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? "Hide" : "Show"}
        tabIndex={-1}
      >
        {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const Field = ({ label, optional, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
      {label}
      {optional && <span className="ml-1.5 font-normal lowercase tracking-normal text-slate-400">optional</span>}
    </label>
    {children}
  </div>
);

// Reusable section card with an iconed header. Renders as a <form> when onSubmit
// is supplied, otherwise a plain container.
const SectionCard = ({ title, desc, icon: Icon, action, onSubmit, children, tone = "violet" }) => {
  const toneClasses =
    tone === "teal"
      ? {
          icon: "bg-[#E9FBF7] text-[#0F766E]",
          border: "border-[#DFF6F1]",
          accent: "from-s360-teal to-s360-violet",
        }
      : {
          icon: "bg-s360-violet-50 text-s360-violet",
          border: "border-[#EAF2F1]",
          accent: "from-s360-violet to-s360-teal",
        };

  const headerRight = action;
  const body = (
    <>
      <div className={`border-b ${toneClasses.border} px-5 py-4 sm:px-6`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {Icon && (
              <span
                className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${toneClasses.icon}`}
              >
                <Icon className="h-[18px] w-[18px]" />
              </span>
            )}
            <div className="flex min-w-0 flex-col">
              <h3 className="m-0 truncate text-[15px] font-semibold text-slate-900">{title}</h3>
              {desc && <span className="truncate text-xs text-slate-500">{desc}</span>}
            </div>
          </div>
          {headerRight}
        </div>
      </div>
      {children}
    </>
  );

  if (onSubmit)
    return (
      <form
        onSubmit={onSubmit}
        className="flex flex-col overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_20px_55px_-35px_rgba(15,23,42,0.45)]"
      >
        <div className={`h-1.5 w-full bg-gradient-to-r ${toneClasses.accent}`} />
        {body}
      </form>
    );
  return (
    <div className="flex flex-col overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_20px_55px_-35px_rgba(15,23,42,0.45)]">
      <div className={`h-1.5 w-full bg-gradient-to-r ${toneClasses.accent}`} />
      {body}
    </div>
  );
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.user);

  const [first = "", ...restName] = (user?.name || "").trim().split(/\s+/);
  const [details, setDetails] = useState({
    firstName: first || "",
    lastName: restName.join(" ") || "",
    email: user?.email || "",
    phone: user?.mobile || "",
    company: user?.company_name || "",
    title: user?.title || "",
  });
  const [savedFlash, setSavedFlash] = useState(false);

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwErr, setPwErr] = useState("");
  const [pwDone, setPwDone] = useState(false);

  // Billing address — fetched from the account endpoint, saved via the billing API.
  const [billing, setBilling] = useState({
    billing_email: "",
    registered_business_name: "",
    billing_line1: "",
    billing_line2: "",
    billing_city: "",
    billing_state: "",
    billing_postal_code: "",
    billing_country: "India",
    gstin: "",
  });
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingFlash, setBillingFlash] = useState(false);

  const set = (k, v) => setDetails((p) => ({ ...p, [k]: v }));
  const setB = (k, v) => setBilling((p) => ({ ...p, [k]: v }));
  const setP = (k, v) => {
    setPw((p) => ({ ...p, [k]: v }));
    setPwErr("");
    setPwDone(false);
  };

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await getAccountAPI();
        if (ignore) return;
        const a = res?.data?.account;
        if (a) {
          setBilling((p) => ({
            ...p,
            billing_email: a.billing_email || a.email || "",
            registered_business_name: a.registered_business_name || a.company || "",
            billing_line1: a.billing_line1 || a.address || "",
            billing_line2: a.billing_line2 || "",
            billing_city: a.billing_city || "",
            billing_state: a.billing_state || "",
            billing_postal_code: a.billing_postal_code || "",
            billing_country: a.billing_country || "India",
            gstin: a.gstin || "",
          }));
        }
      } catch (e) {
        /* non-fatal — the form just starts empty */
      } finally {
        if (!ignore) setLoadingAccount(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const role = user?.role_name || "Member";
  const initials = getInitials(details.firstName, details.lastName, user?.name);
  const fullName = `${details.firstName} ${details.lastName}`.trim() || user?.name || "—";
  const memberSince = user?.created_at ? formatDate(user.created_at) : null;
  const score = pwScore(pw.next);

  const saveDetails = (e) => {
    e.preventDefault();
    // Reflect edits locally so the header/avatar update immediately.
    // TODO: persist to an update-profile API endpoint when available.
    dispatch(
      setUserTokenAction({
        user: {
          ...user,
          name: fullName,
          email: details.email,
          mobile: details.phone,
          company_name: details.company,
          title: details.title,
        },
        token,
      }),
    );
    setSavedFlash(true);
    iziToast.success({ title: "Saved", message: "Profile details updated", position: "topCenter" });
    setTimeout(() => setSavedFlash(false), 2200);
  };

  const saveBillingDetails = async (e) => {
    e.preventDefault();
    if (savingBilling) return;
    setSavingBilling(true);
    try {
      const res = await saveBillingAPI(billing);
      if (res?.status) {
        setBillingFlash(true);
        iziToast.success({
          title: "Saved",
          message: "Billing address updated",
          position: "topCenter",
        });
        setTimeout(() => setBillingFlash(false), 2200);
      } else {
        iziToast.error({
          title: "Error",
          message: res?.message || "Couldn't save the billing address. Please try again.",
          position: "topCenter",
        });
      }
    } catch (err) {
      iziToast.error({
        title: "Error",
        message: "Something went wrong, please try again later.",
        position: "topCenter",
      });
    } finally {
      setSavingBilling(false);
    }
  };

  const updatePassword = (e) => {
    e.preventDefault();
    if (!pw.current) return setPwErr("Enter your current password.");
    if (pw.next.length < 8) return setPwErr("New password must be at least 8 characters.");
    if (score < 2) return setPwErr("Please choose a stronger password.");
    if (pw.next !== pw.confirm) return setPwErr("New password and confirmation do not match.");
    // TODO: call the change-password API when available.
    setPwErr("");
    setPwDone(true);
    setPw({ current: "", next: "", confirm: "" });
    iziToast.success({ title: "Updated", message: "Password updated", position: "topCenter" });
  };

  const savedChip = (show) =>
    show ? (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
        <Check className="h-3.5 w-3.5" /> Saved
      </span>
    ) : null;

  const profileCompletion = Math.min(
    100,
    55 +
      (details.firstName ? 10 : 0) +
      (details.lastName ? 10 : 0) +
      (details.email ? 10 : 0) +
      (details.phone ? 5 : 0) +
      (details.company ? 5 : 0) +
      (details.title ? 5 : 0),
  );
  const securityLabel = score >= 3 ? "Strong" : score >= 2 ? "Solid" : "Needs attention";
  const securityTone = score >= 3 ? "text-emerald-700 bg-emerald-50" : score >= 2 ? "text-amber-700 bg-amber-50" : "text-rose-700 bg-rose-50";

  return (
    <PageWrapper>
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-1 pb-4 sm:px-2 lg:pb-8">
        <div className="overflow-hidden rounded-[32px] border border-slate-200/80 bg-white shadow-[0_25px_80px_-35px_rgba(17,24,39,0.45)]">
          <div className="h-32 bg-gradient-to-r from-s360-violet via-[#6B4FE0] to-s360-teal" />
          <div className="px-5 pb-6 pt-3 sm:px-4 sm:pb-4">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-end">
                <div className="relative -mt-12 shrink-0">
                  <span className="inline-flex h-24 w-24 items-center justify-center rounded-full border-4 border-white bg-s360-teal text-[34px] font-bold text-slate-900 shadow-lg">
                    {initials}
                  </span>
                  <button
                    type="button"
                    title="Change photo"
                    className="absolute bottom-0 right-0 inline-flex h-9 w-9 items-center justify-center rounded-full border-[3px] border-white bg-s360-violet text-white shadow-lg transition hover:bg-s360-violet-600"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h1 className="m-0 text-2xl font-bold tracking-[-0.015em] text-slate-900">
                      {fullName}
                    </h1>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F3F0FF] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-s360-violet">
                      <Sparkles className="h-3.5 w-3.5" />
                      {role}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-600">
                    {details.company && (
                      <span className="inline-flex min-w-0 items-center gap-1.5">
                        <Building2 className="h-4 w-4 shrink-0" />
                        <span className="truncate">{details.company}</span>
                      </span>
                    )}
                    {details.email && (
                      <span className="inline-flex min-w-0 items-center gap-1.5">
                        <Mail className="h-4 w-4 shrink-0" />
                        <span className="truncate">{details.email}</span>
                      </span>
                    )}
                    {details.phone && (
                      <span className="inline-flex items-center gap-1.5">
                        <Phone className="h-4 w-4 shrink-0" />
                        <span>{details.phone}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid gap-3 rounded-[24px] border border-slate-200 bg-slate-50/80 p-4 sm:grid-cols-2 lg:min-w-[340px]">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Profile strength
                  </div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900">{profileCompletion}%</div>
                  <div className="text-sm text-slate-500">Almost there</div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Account status
                  </div>
                  <div className="mt-1 inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <ShieldCheck className="h-4 w-4 text-s360-teal" /> Protected
                  </div>
                  <div className="text-sm text-slate-500">{memberSince ? `Member since ${memberSince}` : "Ready to go"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.35fr_0.65fr]">
          <div className="flex flex-col gap-6">
            <SectionCard
              title="Account details"
              desc="Keep your professional identity fresh"
              icon={User}
              onSubmit={saveDetails}
              action={savedChip(savedFlash)}
            >
              <div className="grid grid-cols-1 gap-4 p-5 sm:p-6 md:grid-cols-2">
                <Field label="First name">
                  <input
                    className={inputCls}
                    value={details.firstName}
                    onChange={(e) => set("firstName", e.target.value)}
                    placeholder="First name"
                  />
                </Field>
                <Field label="Last name">
                  <input
                    className={inputCls}
                    value={details.lastName}
                    onChange={(e) => set("lastName", e.target.value)}
                    placeholder="Last name"
                  />
                </Field>
                <Field label="Email address">
                  <input
                    className={inputCls}
                    type="email"
                    value={details.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@company.com"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    className={inputCls}
                    value={details.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="Mobile number"
                  />
                </Field>
                <Field label="Company">
                  <input
                    className={inputCls}
                    value={details.company}
                    onChange={(e) => set("company", e.target.value)}
                    placeholder="Company"
                  />
                </Field>
                <Field label="Job title">
                  <input
                    className={inputCls}
                    value={details.title}
                    onChange={(e) => set("title", e.target.value)}
                    placeholder="Job title"
                  />
                </Field>
              </div>
              <div className="flex justify-end px-5 pb-5 sm:px-6">
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-s360-violet px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(95,71,201,0.7)] transition hover:bg-s360-violet-600"
                >
                  Save changes
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </SectionCard>

            <SectionCard
              title="Billing address"
              desc="Used to invoice your distributor orders"
              icon={MapPin}
              onSubmit={saveBillingDetails}
              tone="teal"
              action={
                loadingAccount ? (
                  <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                    <Spinner bare size={16} /> Loading…
                  </span>
                ) : (
                  savedChip(billingFlash)
                )
              }
            >
              <div className="grid grid-cols-1 gap-4 p-5 sm:p-6 md:grid-cols-2">
                <Field label="Billing email" className="md:col-span-2">
                  <input
                    className={inputCls}
                    type="email"
                    value={billing.billing_email}
                    onChange={(e) => setB("billing_email", e.target.value)}
                    placeholder="billing@company.com"
                  />
                </Field>
                <Field label="Registered business name" className="md:col-span-2">
                  <input
                    className={inputCls}
                    value={billing.registered_business_name}
                    onChange={(e) => setB("registered_business_name", e.target.value)}
                    placeholder="Acme Robotics Pvt. Ltd."
                  />
                </Field>
                <Field label="Billing address" className="md:col-span-2">
                  <input
                    className={inputCls}
                    value={billing.billing_line1}
                    onChange={(e) => setB("billing_line1", e.target.value)}
                    placeholder="Street address"
                  />
                </Field>
                <Field label="Apartment, suite, etc." optional className="md:col-span-2">
                  <input
                    className={inputCls}
                    value={billing.billing_line2}
                    onChange={(e) => setB("billing_line2", e.target.value)}
                    placeholder="Unit 4B"
                  />
                </Field>
                <Field label="City">
                  <input
                    className={inputCls}
                    value={billing.billing_city}
                    onChange={(e) => setB("billing_city", e.target.value)}
                    placeholder="Bengaluru"
                  />
                </Field>
                <Field label="State / Province">
                  <input
                    className={inputCls}
                    value={billing.billing_state}
                    onChange={(e) => setB("billing_state", e.target.value)}
                    placeholder="Karnataka"
                  />
                </Field>
                <Field label="Postal code">
                  <input
                    className={inputCls}
                    inputMode="numeric"
                    value={billing.billing_postal_code}
                    onChange={(e) => setB("billing_postal_code", e.target.value)}
                    placeholder="560001"
                  />
                </Field>
                <Field label="Country">
                  <select
                    className={`${inputCls} cursor-pointer`}
                    value={billing.billing_country}
                    onChange={(e) => setB("billing_country", e.target.value)}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="GSTIN / Tax ID" optional className="md:col-span-2">
                  <div className="relative flex items-center">
                    <FileText className="pointer-events-none absolute left-3 h-4 w-4 text-slate-400" />
                    <input
                      className={`${inputCls} w-full pl-9 font-mono tracking-wide`}
                      maxLength={15}
                      value={billing.gstin}
                      onChange={(e) => setB("gstin", e.target.value.toUpperCase().slice(0, 15))}
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </div>
                </Field>
              </div>
              <div className="flex justify-end px-5 pb-5 sm:px-6">
                <button
                  type="submit"
                  disabled={savingBilling || loadingAccount}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-s360-violet px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(95,71,201,0.7)] transition hover:bg-s360-violet-600 disabled:opacity-60"
                >
                  <CreditCard className="h-4 w-4" />
                  {savingBilling ? "Saving…" : "Save billing address"}
                </button>
              </div>
            </SectionCard>
          </div>

          <div className="flex flex-col gap-6">
            <SectionCard title="Security overview" desc="A quick view of your protection" icon={ShieldCheck} tone="teal">
              <div className="p-5 sm:p-6">
                <div className="rounded-[22px] border border-[#DFF6F1] bg-[#F7FFFD] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Password health</div>
                      <div className="text-sm text-slate-500">{securityLabel} protection</div>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${securityTone}`}>
                      {securityLabel}
                    </span>
                  </div>
                  <div className="mt-4 flex gap-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="h-1.5 flex-1 rounded-full"
                        style={{ background: i <= score ? STRENGTH_COLOR[score] : "#D8EDE9" }}
                      />
                    ))}
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                    <div className="rounded-xl bg-s360-violet-50 p-2 text-s360-violet">
                      <BadgeCheck className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Verified account</div>
                      <div className="text-xs text-slate-500">
                        Your contact details are synced and ready for support.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                    <div className="rounded-xl bg-[#E9FBF7] p-2 text-[#0F766E]">
                      <Clock3 className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Fast updates</div>
                      <div className="text-xs text-slate-500">
                        Changes are applied instantly and reflected across your workspace.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Password" desc="Update your login credentials" icon={Lock} onSubmit={updatePassword}>
              <div className="p-5 sm:p-6">
                <div className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4 text-sm text-slate-600">
                  Choose a strong password you don't use elsewhere. Keep it unique and easy for you to remember.
                </div>
                <div className="mt-4 flex flex-col gap-4">
                  <Field label="Current password">
                    <PwInput
                      value={pw.current}
                      onChange={(v) => setP("current", v)}
                      placeholder="Enter current password"
                      invalid={pwErr.includes("current")}
                    />
                  </Field>
                  <Field label="New password">
                    <PwInput
                      value={pw.next}
                      onChange={(v) => setP("next", v)}
                      placeholder="Enter new password"
                      invalid={!!pwErr && pwErr.includes("password") && !pwErr.includes("current")}
                    />
                    {pw.next && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="flex flex-1 gap-1">
                          {[1, 2, 3, 4].map((i) => (
                            <i
                              key={i}
                              className="h-1.5 flex-1 rounded-full"
                              style={{ background: i <= score ? STRENGTH_COLOR[score] : "#D8EDE9" }}
                            />
                          ))}
                        </span>
                        <span className="text-[11px] font-semibold" style={{ color: STRENGTH_COLOR[score] || "#4E6763" }}>
                          {STRENGTH[score]}
                        </span>
                      </div>
                    )}
                  </Field>
                  <Field label="Confirm new password">
                    <PwInput
                      value={pw.confirm}
                      onChange={(v) => setP("confirm", v)}
                      placeholder="Re-enter new password"
                      invalid={pwErr.includes("match")}
                    />
                  </Field>

                  {pwErr && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                      {pwErr}
                    </div>
                  )}
                  {pwDone && (
                    <div className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                      <Check className="h-4 w-4" /> Your password has been updated.
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 px-5 pb-5 sm:px-6">
                <button
                  type="button"
                  onClick={() => {
                    setPw({ current: "", next: "", confirm: "" });
                    setPwErr("");
                    setPwDone(false);
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-s360-violet px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(95,71,201,0.7)] transition hover:bg-s360-violet-600"
                >
                  <Lock className="h-4 w-4" /> Update
                </button>
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default ProfilePage;
